import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useToast, type ToastItem, type ToastSeverity } from '../hooks/useToast'

/* ───── Severity configuration ───── */

const severityStyles: Record<ToastSeverity, string> = {
  success: 'border-l-[var(--color-success)]',
  error: 'border-l-[var(--color-danger)]',
  warning: 'border-l-[var(--color-warning)]',
  info: 'border-l-[var(--color-info)]',
}

const severityIconColors: Record<ToastSeverity, string> = {
  success: 'text-[var(--color-success)]',
  error: 'text-[var(--color-danger)]',
  warning: 'text-[var(--color-warning)]',
  info: 'text-[var(--color-info)]',
}

const DEFAULT_DURATION = 5000

/* ───── Icons ───── */

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  )
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  )
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}

const severityIcons: Record<ToastSeverity, typeof CheckCircleIcon> = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: AlertTriangleIcon,
  info: InfoIcon,
}

/* ───── Single Toast ───── */

interface ToastProps {
  toast: ToastItem
  onDismiss: (id: string) => void
}

export function Toast({ toast: t, onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(false)
  const [exiting, setExiting] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const Icon = severityIcons[t.severity]
  const duration = t.duration ?? DEFAULT_DURATION

  // Enter animation
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  // Auto-dismiss
  useEffect(() => {
    if (duration <= 0) return
    timerRef.current = setTimeout(() => {
      handleDismiss()
    }, duration)
    return () => clearTimeout(timerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  const handleDismiss = () => {
    setExiting(true)
    setTimeout(() => onDismiss(t.id), 200)
  }

  return (
    <div
      role="alert"
      data-testid="toast"
      className={`
        pointer-events-auto w-80 overflow-hidden
        rounded-[var(--radius-lg)] border-l-4
        bg-[var(--color-card)] text-[var(--color-card-foreground)]
        shadow-[var(--shadow-lg)]
        transition-all duration-200 ease-out
        ${severityStyles[t.severity]}
        ${visible && !exiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className="flex items-start gap-3 p-3">
        <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${severityIconColors[t.severity]}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium" data-testid="toast-title">{t.title}</p>
          {t.description && (
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]" data-testid="toast-description">
              {t.description}
            </p>
          )}
          {t.action && (
            <button
              type="button"
              onClick={t.action.onClick}
              className="mt-2 text-xs font-medium text-[var(--color-accent)] hover:underline"
              data-testid="toast-action"
            >
              {t.action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 rounded-[var(--radius-sm)] p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
          aria-label="Dismiss"
          data-testid="toast-dismiss"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

/* ───── Toast Container ───── */

export function ToastContainer() {
  const toasts = useToast((s) => s.toasts)
  const dismiss = useToast((s) => s.dismiss)

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col-reverse gap-2"
      data-testid="toast-container"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>,
    document.body,
  )
}
